import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  Button,
  Divider,
  Grid,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Typography,
  Box,
  Chip,
  Tab,
  Tabs,
  IconButton,
  TextField,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  useGetTemplateQuery,
  useGetRolesQuery,
  useGetUsersQuery,
  useUpdateTemplateMutation,
  UpdateTemplateDTO,
  useCreateChannelMutation,
  CreateChannelDTO,
  useDeleteChannelMutation,
} from '../../../../api';
import { InfoFieldChangeHandler, InfoField } from '../../../../components';
import { HeadCell, Row, RowCell } from '../../../../components/table/types';
import { NotificationTemplateEventName } from '../../../../enums';
import { TestModal } from '../TestModal';
import { Table } from '../../../../components/table/table';
import { NotificationTemplateChannel } from '../../../../types';
import Title from '../../../../components/title/Title';
import { DeleteChannelModal } from '../DeleteChannelModal';

export type ChannelsProps = {
  notification_template_channels?: NotificationTemplateChannel[];
  refresh: () => void;
};

export const Channels: FC<ChannelsProps> = ({ notification_template_channels, refresh }) => {
  const { t } = useTranslation();
  const { eventName } = useParams<{ eventName: `${NotificationTemplateEventName}` }>();
  const { enqueueSnackbar } = useSnackbar();

  // api
  const { data: roles } = useGetRolesQuery();
  const { data: users } = useGetUsersQuery();
  const [createChannel, { isLoading: isCreatingChannel }] = useCreateChannelMutation();
  const [deleteChannel, { isLoading: isDeletingChannel }] = useDeleteChannelMutation();

  const [activeTab, setActiveTab] = useState<number>(1);
  const [activeChennel, setActiveChennel] = useState<number | null>(null);
  const [channelField, setChannelField] = useState<CreateChannelDTO['body'] | null>(null);

  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (app: number) => {
    setDeleteModalShown(true);
    setActiveChennel(app);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveChennel(null);
  };

  const onDelete = async () => {
    if (!activeChennel || !eventName) return;

    const args = {
      event: eventName,
      channel_id: activeChennel,
    };

    try {
      const res = await deleteChannel(args).unwrap();
      closeDeleteModal();
      refresh();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const onCreate = async () => {
    if (!channelField || !eventName) return;

    const args = {
      event: eventName,
      body: {
        ...channelField,
      },
    };

    try {
      const res = await createChannel(args).unwrap();
      setChannelField(null);
      refresh();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const rolesSelectOptions = roles?.roles.map((role) => {
    return (
      <MenuItem key={role.id} value={role.id}>
        {role.name}
      </MenuItem>
    );
  });
  const usersSelectOptions = users?.users.map((user) => {
    return (
      <MenuItem key={user.uid} value={user.uid}>
        {`${user.firstName} ${user.lastName} (${user.email})`}
      </MenuItem>
    );
  });

  const roleHeadCells: HeadCell[] = [
    { id: '1', label: t('notification-template.channels.id') },
    { id: '2', label: t('notification-template.channels.role') },
    { id: '3', label: t('notification-template.channels.action') },
  ];

  const userHeadCells: HeadCell[] = [
    { id: '1', label: t('notification-template.channels.id') },
    { id: '2', label: t('notification-template.channels.user') },
    { id: '3', label: t('notification-template.channels.action') },
  ];

  const emailHeadCells: HeadCell[] = [
    { id: '1', label: t('notification-template.channels.id') },
    { id: '2', label: t('notification-template.channels.email') },
    { id: '3', label: t('notification-template.channels.action') },
  ];

  const roleRows: Row[] | undefined = notification_template_channels
    ?.filter((it) => it.type === 'ROLE')
    ?.map((it) => {
      const { id, role_id } = it;

      const cells: RowCell[] = [
        { name: 'id', value: id },
        { name: 'role', value: roles?.roles.find((it) => it.id === role_id)?.name },
        {
          name: 'actions',
          component: (
            <IconButton aria-label="delete" color="error" onClick={() => openDeleteModal(it.id)}>
              <DeleteIcon />
            </IconButton>
          ),
        },
      ];

      return { cells };
    });

  const emailRows: Row[] | undefined = notification_template_channels
    ?.filter((it) => it.type === 'EMAIL')
    ?.map((it) => {
      const { email, id } = it;

      const cells: RowCell[] = [
        { name: 'id', value: id },
        { name: 'email', value: email },
        {
          name: 'actions',
          component: (
            <IconButton aria-label="delete" color="error" onClick={() => openDeleteModal(it.id)}>
              <DeleteIcon />
            </IconButton>
          ),
        },
      ];

      return { cells };
    });

  const userRows: Row[] | undefined = notification_template_channels
    ?.filter((it) => it.type === 'USER')
    ?.map((it) => {
      const { id, user_uid } = it;

      const cells: RowCell[] = [
        { name: 'id', value: id },
        { name: 'status', value: user_uid },
        {
          name: 'actions',
          component: (
            <IconButton aria-label="delete" color="error" onClick={() => openDeleteModal(it.id)}>
              <DeleteIcon />
            </IconButton>
          ),
        },
      ];

      return { cells };
    });

  return (
    <>
      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
        <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Title>{t('notification-template.channels.title')}</Title>
          <Tabs value={activeTab} variant="scrollable" scrollButtons="auto">
            <Tab
              label={t('notification-template.channels.role')}
              value={1}
              onClick={() => setActiveTab(1)}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              label={t('notification-template.channels.user')}
              value={2}
              onClick={() => setActiveTab(2)}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              label={t('notification-template.channels.email')}
              value={3}
              onClick={() => setActiveTab(3)}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          {activeTab === 1 && (
            <Stack direction="column" spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormControl variant="standard">
                  <InputLabel>{t('notification-template.channels.role')}</InputLabel>
                  <Select
                    name="role_id"
                    value={channelField?.role_id ?? ''}
                    onChange={(e) =>
                      setChannelField({
                        type: 'ROLE',
                        role_id: Number(e.target.value),
                      })
                    }
                    variant="standard"
                    sx={{ minWidth: '280px', maxWidth: '360px' }}
                  >
                    {rolesSelectOptions}
                  </Select>
                </FormControl>

                <Button variant="contained" onClick={() => onCreate()}>
                  {t('abf-pay-apps.modals.create.create-btn')}
                </Button>
              </Stack>
              <Table headCells={roleHeadCells} rows={roleRows} />
            </Stack>
          )}
          {activeTab === 2 && (
            <Stack direction="column" spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormControl variant="standard">
                  <InputLabel>{t('notification-template.channels.user')}</InputLabel>
                  <Select
                    name="user_uid"
                    value={channelField?.user_uid ?? ''}
                    onChange={(e) =>
                      setChannelField({
                        type: 'USER',
                        user_uid: e.target.value,
                      })
                    }
                    variant="standard"
                    sx={{ minWidth: '280px', maxWidth: '360px' }}
                  >
                    {usersSelectOptions}
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={() => onCreate()}>
                  {t('abf-pay-apps.modals.create.create-btn')}
                </Button>
              </Stack>
              <Table headCells={userHeadCells} rows={userRows} />
            </Stack>
          )}
          {activeTab === 3 && (
            <Stack direction="column" spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  name="EMAIL"
                  value={channelField?.email ?? ''}
                  onChange={(e) =>
                    setChannelField({
                      type: 'EMAIL',
                      email: e.target.value,
                    })
                  }
                  label={t('notification-template.channels.email')}
                />
                <Button variant="contained" onClick={() => onCreate()}>
                  {t('abf-pay-apps.modals.create.create-btn')}
                </Button>
              </Stack>
              <Table headCells={emailHeadCells} rows={emailRows} />
            </Stack>
          )}
        </Paper>
      </Grid>

      <DeleteChannelModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={onDelete}
        isLoading={isDeletingChannel}
      />
    </>
  );
};
